import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DSIRecommendationModal.css';

export const TREATMENT_RECOMMENDED = 'treatment_recommended';
export const TREATMENT_NOT_RECOMMENDED = 'treatment_not_recommended_at_this_time';

export default function DSIRecommendationModal({
  open,
  patient,
  file,
  saving = false,
  error = '',
  onSave,
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (open) {
      setSelected(file?.treatment_recommendation || '');
      setLocalError('');
    }
  }, [open, file]);

  if (!open) return null;

  const patientName = [patient?.first_name, patient?.last_name].filter(Boolean).join(' ');
  const processingType = file?.processing_type || '';

  const submit = () => {
    if (!selected) {
      setLocalError(t('dsiRec.required'));
      return;
    }
    setLocalError('');
    onSave?.(selected);
  };

  return (
    <div className="dsi-rec-backdrop" role="presentation">
      <section
        className="dsi-rec-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dsi-rec-title"
      >
        <div className="dsi-rec-header">
          <span className="dsi-rec-eyebrow">{t('dsiRec.uploadComplete')}</span>
          <h3 id="dsi-rec-title">{t('dsiRec.title')}</h3>
          <p>{t('dsiRec.subtitle')}</p>
        </div>

        <div className="dsi-rec-context">
          <div>
            <span>{t('dsiRec.patient')}</span>
            <strong>{patientName || t('nA')}</strong>
          </div>
          {processingType && (
            <div>
              <span>{t('dsiRec.type')}</span>
              <strong>{processingType}</strong>
            </div>
          )}
        </div>

        <div className="dsi-rec-options" role="radiogroup" aria-label={t('dsiRec.aria')}>
          <button
            type="button"
            role="radio"
            aria-checked={selected === TREATMENT_RECOMMENDED}
            className={`dsi-rec-option dsi-rec-option--recommended ${selected === TREATMENT_RECOMMENDED ? 'is-selected' : ''}`}
            onClick={() => setSelected(TREATMENT_RECOMMENDED)}
            disabled={saving}
          >
            <span className="dsi-rec-option-mark" aria-hidden="true">
              {selected === TREATMENT_RECOMMENDED ? '✓' : ''}
            </span>
            <span className="dsi-rec-option-copy">
              <strong>{t('recommendation.recommended')}</strong>
              <small>{t('dsiRec.recommendedHelp')}</small>
            </span>
            {selected === TREATMENT_RECOMMENDED && <span className="dsi-rec-selected-label">{t('dsiRec.selected')}</span>}
          </button>

          <button
            type="button"
            role="radio"
            aria-checked={selected === TREATMENT_NOT_RECOMMENDED}
            className={`dsi-rec-option dsi-rec-option--not-recommended ${selected === TREATMENT_NOT_RECOMMENDED ? 'is-selected' : ''}`}
            onClick={() => setSelected(TREATMENT_NOT_RECOMMENDED)}
            disabled={saving}
          >
            <span className="dsi-rec-option-mark" aria-hidden="true">
              {selected === TREATMENT_NOT_RECOMMENDED ? '✓' : ''}
            </span>
            <span className="dsi-rec-option-copy">
              <strong>{t('recommendation.notRecommended')}</strong>
              <small>{t('dsiRec.notRecommendedHelp')}</small>
            </span>
            {selected === TREATMENT_NOT_RECOMMENDED && <span className="dsi-rec-selected-label">{t('dsiRec.selected')}</span>}
          </button>
        </div>

        {(localError || error) && <div className="dsi-rec-error">{localError || error}</div>}

        <div className="dsi-rec-footer">
          <button type="button" className="dsi-rec-save" onClick={submit} disabled={saving}>
            {saving ? t('dsiRec.saving') : t('dsiRec.saveFinish')}
          </button>
        </div>
      </section>
    </div>
  );
}
